// ============================================
// Chat Search Component
// Search messages within current conversation
// ============================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useChat } from './ChatProvider';

/**
 * Search panel for finding messages in current channel/DM
 */
const ChatSearch = () => {
  const {
    searchQuery,
    setSearchQuery,
    searchResults,
    searchLoading,
    searchFilters,
    setSearchFilters,
    searchOpen,
    setSearchOpen,
    searchTotalCount,
    searchHasMore,
    searchMessages,
    clearSearch,
    jumpToMessage,
    currentChannel
  } = useChat();

  const [localQuery, setLocalQuery] = useState(searchQuery);
  const [showFilters, setShowFilters] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Focus input when search opens
  useEffect(() => {
    if (searchOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [searchOpen]);

  // Sync local query with context
  useEffect(() => {
    setLocalQuery(searchQuery);
  }, [searchQuery]);

  // Debounced search
  const handleQueryChange = useCallback((value) => {
    setLocalQuery(value);
    setSearchQuery(value);

    // Clear previous timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Set new debounced search
    if (value.trim().length >= 2) {
      debounceRef.current = setTimeout(() => {
        searchMessages(value, searchFilters);
      }, 300);
    } else if (value.trim().length === 0) {
      // Clear results when query is empty
      searchMessages('', searchFilters);
    }
  }, [setSearchQuery, searchMessages, searchFilters]);

  // Handle filter changes
  const handleFilterChange = useCallback((filterKey, value) => {
    const newFilters = { ...searchFilters, [filterKey]: value || null };
    setSearchFilters(newFilters);

    // Re-search with new filters
    if (localQuery.trim().length >= 2) {
      searchMessages(localQuery, newFilters);
    }
  }, [searchFilters, setSearchFilters, localQuery, searchMessages]);

  // Load more results
  const loadMore = useCallback(() => {
    if (!searchLoading && searchHasMore) {
      searchMessages(localQuery, searchFilters, searchResults.length);
    }
  }, [searchLoading, searchHasMore, localQuery, searchFilters, searchResults.length, searchMessages]);

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return `Today at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  // Highlight matching text in content
  const highlightMatch = (text, query) => {
    if (!query || !text) return text;

    const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={i} className="bg-yellow-200 text-gray-900 px-0.5 rounded">{part}</mark>
      ) : (
        part
      )
    );
  };

  // Don't render if search is closed
  if (!searchOpen) return null;

  return (
    <div className="border-b border-gray-200 bg-white">
      {/* Search Input */}
      <div className="p-2">
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={localQuery}
            onChange={(e) => handleQueryChange(e.target.value)}
            placeholder={`Search in ${currentChannel?.display_name || 'conversation'}...`}
            className="w-full pl-9 pr-20 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {/* Search icon */}
          <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>

          {/* Right side buttons */}
          <div className="absolute right-2 top-1.5 flex items-center space-x-1">
            {/* Filter toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-1.5 rounded hover:bg-gray-100 transition-colors ${showFilters ? 'bg-blue-100 text-blue-600' : 'text-gray-400'}`}
              title="Filters"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
            </button>

            {/* Clear button */}
            {localQuery && (
              <button
                onClick={clearSearch}
                className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                title="Clear search"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}

            {/* Close button */}
            <button
              onClick={() => setSearchOpen(false)}
              className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
              title="Close search"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-2 p-2 bg-gray-50 rounded-lg space-y-2">
            <div className="flex flex-wrap gap-2">
              {/* Date From */}
              <div className="flex items-center space-x-1">
                <label className="text-xs text-gray-500">From:</label>
                <input
                  type="date"
                  value={searchFilters.dateFrom || ''}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  className="text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* Date To */}
              <div className="flex items-center space-x-1">
                <label className="text-xs text-gray-500">To:</label>
                <input
                  type="date"
                  value={searchFilters.dateTo || ''}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  className="text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* Sender Type */}
              <div className="flex items-center space-x-1">
                <label className="text-xs text-gray-500">From:</label>
                <select
                  value={searchFilters.senderType || ''}
                  onChange={(e) => handleFilterChange('senderType', e.target.value)}
                  className="text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">Anyone</option>
                  <option value="student">Students</option>
                  <option value="recruiter">Recruiters</option>
                  <option value="alumni">Alumni</option>
                  <option value="admin">Admins</option>
                </select>
              </div>

              {/* Clear Filters */}
              {(searchFilters.dateFrom || searchFilters.dateTo || searchFilters.senderType) && (
                <button
                  onClick={() => {
                    setSearchFilters({ dateFrom: null, dateTo: null, senderType: null, senderId: null });
                    if (localQuery.trim().length >= 2) {
                      searchMessages(localQuery, {});
                    }
                  }}
                  className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Search Results */}
      {localQuery.trim().length >= 2 && (
        <div className="max-h-64 overflow-y-auto border-t border-gray-100">
          {/* Loading State */}
          {searchLoading && searchResults.length === 0 && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          )}

          {/* No Results */}
          {!searchLoading && searchResults.length === 0 && localQuery.trim().length >= 2 && (
            <div className="py-8 text-center text-gray-500">
              <svg className="w-8 h-8 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm">No messages found for "{localQuery}"</p>
            </div>
          )}

          {/* Results Count */}
          {searchResults.length > 0 && (
            <div className="px-3 py-1.5 text-xs text-gray-500 bg-gray-50 border-b border-gray-100">
              {searchTotalCount} {searchTotalCount === 1 ? 'result' : 'results'} found
            </div>
          )}

          {/* Results List */}
          {searchResults.map((result) => (
            <button
              key={result.id}
              onClick={() => jumpToMessage(result.id)}
              className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
            >
              <div className="flex items-start space-x-2">
                {/* Sender avatar placeholder */}
                <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-medium ${
                  result.sender?.user_type === 'student' ? 'bg-blue-500' :
                  result.sender?.user_type === 'recruiter' ? 'bg-green-500' :
                  result.sender?.user_type === 'alumni' ? 'bg-purple-500' :
                  result.sender?.user_type === 'admin' ? 'bg-red-500' : 'bg-gray-500'
                }`}>
                  {result.sender?.display_name?.substring(0, 2).toUpperCase() || '??'}
                </div>

                <div className="flex-1 min-w-0">
                  {/* Sender and date */}
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {result.sender?.display_name || 'Unknown'}
                    </span>
                    <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
                      {formatDate(result.created_at)}
                    </span>
                  </div>

                  {/* Message snippet with highlight */}
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {highlightMatch(result.content_snippet || result.content, localQuery)}
                  </p>

                  {/* Thread indicator */}
                  {result.is_thread_reply && (
                    <span className="inline-flex items-center mt-1 text-xs text-blue-600">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                      </svg>
                      Thread reply
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))}

          {/* Load More */}
          {searchHasMore && (
            <div className="p-2 text-center">
              <button
                onClick={loadMore}
                disabled={searchLoading}
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-50"
              >
                {searchLoading ? 'Loading...' : 'Load more results'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Hint when empty */}
      {localQuery.trim().length < 2 && localQuery.trim().length > 0 && (
        <div className="px-3 py-2 text-xs text-gray-400 text-center border-t border-gray-100">
          Type at least 2 characters to search
        </div>
      )}
    </div>
  );
};

export default ChatSearch;
